import React, { useCallback, useEffect, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Breadcrumb, Button, Row, Space, Spin, Typography } from 'antd';
import { Link, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Company } from 'src/features/companies/companies.types';

import EditableField from 'src/components/fields/EditableField';
import { DeleteOutlined, FileDoneOutlined, ShopTwoTone } from '@ant-design/icons';
import { fetchCompany } from 'src/features/companies/companies.state';
import { useCompanyById } from 'src/features/companies/companies.hooks';
import { useOfferById } from 'src/features/companyOffers/companyOffers.hooks';
import { CompanyOffer, DraftCompanyOffer } from 'src/features/companyOffers/companyOffers.types';
import { deleteOffer, fetchOffers, updateOffer } from 'src/features/companyOffers/companyOffers.state';
import TextArea from 'antd/es/input/TextArea';
import format from 'date-fns/format';
import MainLayout from 'src/components/layouts/MainLayout';
import { AppDispatch } from '../../../../index';

const CompanyOfferPage: React.FC = () => {
  const { langCode, companyId } = useParams() as {
    langCode: string;
    companyId: string;
  };
  const dispatch: AppDispatch = useDispatch();
  const offer = useOfferById(langCode) as CompanyOffer;
  const company = useCompanyById(companyId) as Company;

  const [draftOffer, setDraftOffer] = useState<DraftCompanyOffer>({ ...offer });

  const parseDate = useCallback((date: string) => {
    if (!date) {
      return '';
    }
    return format(new Date(date), 'd MMMM y hh:mm');
  }, []);

  const handleOfferTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDraftOffer(draft => ({ ...draft, text: e.target.value }));
  }, []);

  const handleOfferUpdate = useCallback(() => {
    dispatch(updateOffer({ companyId, code: langCode, data: draftOffer }));
  }, [companyId, dispatch, draftOffer, langCode]);

  const handleDeleteButtonClick = useCallback(() => {
    dispatch(deleteOffer({ code: langCode, companyId }));
  }, [companyId, dispatch, langCode]);

  useEffect(() => {
    setDraftOffer({ ...offer });
  }, [offer]);

  return (
    <MainLayout>
      <div className={styles.mainGrid}>
        <div className={styles.breadCrumbs}>
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to='/commercial/companies'>
                <Space>
                  <ShopTwoTone />
                  Компании
                </Space>
              </Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to={`/commercial/companies/${companyId}`}>{company.name}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Space>
                <FileDoneOutlined />
                Офферы
              </Space>
            </Breadcrumb.Item>
            <Breadcrumb.Item>{offer.langCode}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className={styles.contentArea}>
          <Typography.Title level={2} className={styles.title}>
            {offer.langCode}
          </Typography.Title>
          <EditableField value={offer.text}>
            <TextArea value={draftOffer.text} onChange={handleOfferTextChange} onBlur={handleOfferUpdate} />
          </EditableField>
          <Space size='small' />
        </div>
        <div className={styles.sidebar}>
          <Space direction='vertical' size='small'>
            <Typography.Text className={styles.footerDateStamp}>Создано: {parseDate(offer.createdAt)}</Typography.Text>
            <Typography.Text className={styles.footerDateStamp}>
              Обновлено: {parseDate(offer.updatedAt)}
            </Typography.Text>
            <Button type='link' onClick={handleDeleteButtonClick} danger size='small' style={{ padding: 0 }}>
              <DeleteOutlined /> Удалить оффер
            </Button>
          </Space>
        </div>
      </div>
    </MainLayout>
  );
};

const CompanyOfferPageBlock: React.FC = () => {
  const { langCode, companyId } = useParams() as {
    langCode: string;
    companyId: string;
  };
  const dispatch = useDispatch();
  const offer = useOfferById(langCode);
  const company = useCompanyById(companyId);

  useEffect(() => {
    dispatch(fetchCompany({ companyId }));
    dispatch(fetchOffers({ companyId }));
  }, [companyId, dispatch]);

  if (!offer || !company) {
    return (
      <Row align={'middle'} justify={'center'} style={{ minHeight: '100vh' }}>
        <Spin size={'large'} />
      </Row>
    );
  }

  return <CompanyOfferPage />;
};

export default CompanyOfferPageBlock;
